import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { LoaderCircle } from 'lucide-preact';
import { SetStateAction } from 'preact/compat';
import { Dispatch, useMemo, useState } from 'preact/hooks';

import { Button } from '@/components/Buttons';
import { ProductFeaturesList, ProductFeaturesListItem } from '@/components/products/ProductFeatureList';
import ProductIntervalSelector from '@/components/products/ProductIntervalSelector';
import ProductPrice from '@/components/products/ProductPrice';
import { ProductType } from '@/components/products/ProductsPromptModal/redux/types';
import { parseBenefits } from '@/components/products/utils';
import { getProductsQueryOptions } from '@/queries/products';
import { ParsedUserProduct, Product, ProductInterval } from '@/queries/products/types';

import UserProductImage from './UserProductImage';

interface PromotedProductSectionProps {
  productType: ProductType;
  userProduct: ParsedUserProduct;
  onSwitch: (product: Product) => void;
  isPending: boolean;
  onResume: () => void;
  processingButtonId: string | null;
  setProcessingButtonId: Dispatch<SetStateAction<string | null>>;
}

const PromotedProductSection = ({
  productType,
  userProduct,
  onSwitch,
  isPending,
  onResume,
  processingButtonId,
  setProcessingButtonId,
}: PromotedProductSectionProps) => {
  const isPromotingEducatorPass = productType === 'educator-pass';
  const [interval, setInterval] = useState<ProductInterval>('month');

  const { data: productsMap, isLoading } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: isPromotingEducatorPass ? 'monthly-educator,annual-educator' : 'monthly-member,annual-member',
    }),
    select: (data) => {
      return data.list.reduce((acc, product) => {
        if (!product.productLookupKey) return acc;
        acc.set(product.productLookupKey, product);
        return acc;
      }, new Map<string, Product>());
    },
  });

  const monthlyProduct = productsMap?.get('monthly-member') || productsMap?.get('monthly-educator');
  const yearlyProduct = productsMap?.get('annual-member') || productsMap?.get('annual-educator');

  const product = interval === 'month' ? monthlyProduct : yearlyProduct;
  const isSwitchingToProduct = userProduct.scheduledProductId === product?._id;

  const benefits = useMemo(() => parseBenefits(product), [product]);

  if (isLoading || !product) {
    return null;
  }

  return (
    <div className="px-3 py-5 desktop:px-6">
      <p className="preflight preflight-p mb-8 text-lg font-medium text-stone-500 desktop:text-xl">You Might Like</p>
      <div className="flex min-w-0 items-center gap-3 mobile:w-full desktop:grow">
        <UserProductImage className="size-11 shrink-0 desktop:size-[60px]" />
        <div>
          <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">
            {isPromotingEducatorPass ? 'Educator Plan' : 'Explorer Plan'}
          </h6>
          <p className="preflight preflight-p line-clamp-1 text-base text-stone-500 desktop:text-xl">
            {isPromotingEducatorPass ? 'Unlimited learning on top of existing access.' : 'Unlimited discovery and delight.'}
          </p>
        </div>
      </div>
      <div className="my-5 flex items-center gap-4">
        <ProductPrice currency={product.price.currency} price={product.price.price} />
        <ProductIntervalSelector value={interval} onChange={setInterval} />
      </div>
      <ProductFeaturesList className="w-full">
        {benefits.map((benefit, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
        ))}
      </ProductFeaturesList>
      <div className="mt-10 flex w-full items-center justify-center gap-4 mobile:flex-col desktop:justify-between desktop:gap-[60px]">
        <Button
          data-id="promoted-product-button"
          variant={isSwitchingToProduct ? 'outline' : 'default'}
          disabled={isPending}
          type="button"
          className="min-w-40 shrink-0 px-5 py-3 text-base/none font-bold mobile:w-full desktop:order-2"
          onClick={() => {
            setProcessingButtonId('promoted-product-button');
            if (isSwitchingToProduct) {
              onResume();
            } else {
              onSwitch(product);
            }
          }}
        >
          {isPending && processingButtonId === 'promoted-product-button' ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : isSwitchingToProduct ? (
            'Cancel Switch'
          ) : isPromotingEducatorPass ? (
            'Upgrade Plan'
          ) : (
            'Switch Plan'
          )}
        </Button>

        <p className="preflight preflight-p text-base font-normal text-stone-500 desktop:text-xl">
          {isPromotingEducatorPass
            ? 'Your upgraded access starts right away, and you’ll only be charged the prorated difference for the rest of your current billing cycle.'
            : `By switching, you will pay on ${format(
                userProduct.validUntil,
                'MMMM d yyyy',
              )}, after your current plan expires`}
        </p>
      </div>
    </div>
  );
};

export default PromotedProductSection;
